<script lang="ts" setup>
import { inject, ref } from "vue";

import {
    charge,
    energy,
    force,
    frequency,
    length,
    mass,
    partsPer,
    pressure,
    speed,
    temperature,
    time,
    voltage,
    volume,
    volumeFlowRate,
} from "@libTs/units-converter";
import { Converter, Described, Value } from "@libTs/units-converter/utils";
import { Clipper } from "../../helpers/clipper";
import { TranslationService } from "../../services/translation.service";
import { vDropdown as dropdownDirective } from "../../directives/v-dropdown";

const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;
const vDropdown = dropdownDirective;

interface ConversionCategory {
    name: string;
    fn: (value: number) => Converter;
    units: Array<Described>;
}

const UNITCATEGORIES = {
    mass: { name: "mass", fn: mass, units: mass(0).list() },
    partsPer: { name: "partsPer", fn: partsPer, units: partsPer(0).list() },
    length: { name: "length", fn: length, units: length(0).list() },
    volume: { name: "volume", fn: volume, units: volume(0).list() },
    energy: { name: "energy", fn: energy, units: energy(0).list() },
    pressure: { name: "pressure", fn: pressure, units: pressure(0).list() },
    temperature: {
        name: "temperature",
        fn: temperature,
        units: temperature(0).list(),
    },
    time: { name: "time", fn: time, units: time(0).list() },
    charge: { name: "charge", fn: charge, units: charge(0).list() },
    volumeFlowRate: {
        name: "volumeFlowRate",
        fn: volumeFlowRate,
        units: volumeFlowRate(0).list(),
    },
    voltage: { name: "voltage", fn: voltage, units: voltage(0).list() },
    speed: { name: "speed", fn: speed, units: speed(0).list() },
    force: { name: "force", fn: force, units: force(0).list() },
    frequency: { name: "frequency", fn: frequency, units: frequency(0).list() },
};

interface IUnitConvModel {
    category: ConversionCategory | undefined;
    hasError: boolean;
    input: string;
    fromUnit: Described | undefined;
    toUnit: Described | undefined;
    output: Value | undefined;
}

const model = ref<IUnitConvModel>({
    category: undefined,
    hasError: false,
    input: "0",
    fromUnit: undefined,
    toUnit: undefined,
    output: undefined,
});

const onSelectCategory = (category: ConversionCategory): void => {
    model.value.category = category;
    model.value.fromUnit = undefined;
    model.value.toUnit = undefined;
    model.value.output = undefined;

    calculate();
};

function onSelectFromUnit(unit: Described): void {
    model.value.fromUnit = unit;

    calculate();
}

function onSelectToUnit(unit: Described): void {
    model.value.toUnit = unit;

    calculate();
}

function onClickCalculate(): void {
    calculate();
}

function onChangeInput(): void {
    calculate();
}

function calculate(): void {
    // clear previous error
    if (model.value.hasError) {
        model.value.hasError = false;
    }

    if (!model.value.category) {
        model.value.fromUnit = undefined;
        model.value.toUnit = undefined;
        model.value.output = undefined;
        return;
    }

    const categoryFn = model.value.category.fn;
    if (typeof categoryFn !== "function") {
        model.value.fromUnit = undefined;
        model.value.toUnit = undefined;
        model.value.output = undefined;
        return;
    }

    if (!model.value.fromUnit) {
        model.value.toUnit = undefined;
        model.value.output = undefined;
        return;
    }

    if (!model.value.toUnit) {
        model.value.output = undefined;
        return;
    }

    const value = Number.parseFloat(model.value.input);
    if (Number.isNaN(value)) {
        model.value.output = undefined;
        model.value.hasError = true;
        return;
    }
    model.value.output = categoryFn(value)
        .from(model.value.fromUnit.unit)
        .to(model.value.toUnit.unit);
}

function onCopyOutput(): void {
    if (model.value.output) {
        Clipper.write(model.value.output?.value).catch(() => {
            alert("Unable to copy value");
        });
    }
}
</script>

<template>
    <div class="tool-content-inner pt-4">
        <div class="grid grid-cols-3 gap-x-4 gap-y-6">
            <div class="col-span-3">
                <label class="text-f7">{{ t("Category of units") }}</label>
                <div
                    class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2"
                    v-dropdown
                >
                    <div class="input-text rounded-l-s">
                        <input
                            class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-l-s"
                            type="text"
                            autocomplete="off"
                            :value="model.category?.name"
                            disabled
                        />
                    </div>
                    <div class="input-text-btn relative rounded-r-s bg-grey-50">
                        <i
                            class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                        ></i>
                    </div>
                    <div class="input-dropdown-elements bgcolor-white w-full">
                        <div class="input-dropdown-elements-inner">
                            <ul>
                                <li
                                    class="text-small bold"
                                    v-for="(category, key) in UNITCATEGORIES"
                                    @click="onSelectCategory(category)"
                                >
                                    {{ category.name }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Input Unit -->
            <div class="col-span-2">
                <label class="text-f7">{{ t("Source value") }}</label>
                <div class="input-text rounded-l-s mt-2 mb-4">
                    <input
                        class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-l-s"
                        type="text"
                        autocomplete="off"
                        v-model="model.input"
                        @keyup="onChangeInput()"
                    />
                </div>
            </div>
            <div class="col-span-1">
                <label class="text-f7">{{ t("Source unit") }}</label>
                <div
                    class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2 mb-4"
                    :class="{ disabled: !model?.category?.units }"
                    v-dropdown
                >
                    <div class="input-text rounded-l-s">
                        <input
                            class="!px-5 !py-3 w-full bg-grey-50 rounded-s input-text-input"
                            type="text"
                            autocomplete="off"
                            :value="model.fromUnit?.unit"
                            disabled
                        />
                    </div>
                    <div class="input-text-btn relative rounded-r-s bg-grey-50">
                        <i
                            class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                        ></i>
                    </div>
                    <div
                        class="input-dropdown-elements input-dropdown-elements-up bgcolor-white w-full"
                    >
                        <div class="input-dropdown-elements-inner">
                            <ul>
                                <li
                                    class="text-small bold"
                                    v-for="(unit, key) in model?.category
                                        ?.units"
                                    @click="onSelectFromUnit(unit)"
                                >
                                    {{ unit.unit }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Output Unit -->
            <div class="col-span-2">
                <div class="units-converter-input-value">
                    <label class="text-f7">{{ t("Target value") }}</label>

                    <div class="w-full relative mt-2 mb-4">
                        <div
                            class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s h-10"
                        >
                            {{ model?.output?.value }}
                        </div>
                        <i
                            class="far fa-copy text-primary absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                            @click="onCopyOutput()"
                        ></i>
                    </div>
                </div>
            </div>

            <div class="col-span-1">
                <label class="text-f7">{{ t("Target unit") }}</label>
                <div
                    class="input-text-w-btn input-dropdown rounded-s bg-grey-50 mt-2 mb-4"
                    v-dropdown
                    :class="{ disabled: !model?.category?.units }"
                >
                    <div class="input-text rounded-l-s">
                        <input
                            class="!px-5 !py-3 w-full bg-grey-50 rounded-s input-text-input"
                            type="text"
                            autocomplete="off"
                            :value="model?.toUnit?.unit"
                            disabled
                        />
                    </div>
                    <div class="input-text-btn relative rounded-r-s bg-grey-50">
                        <i
                            class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2"
                        ></i>
                    </div>
                    <div
                        class="input-dropdown-elements input-dropdown-elements-up bgcolor-white w-full"
                    >
                        <div class="input-dropdown-elements-inner">
                            <ul>
                                <li
                                    class="text-small bold"
                                    v-for="(unit, key) in model?.category
                                        ?.units"
                                    @click="onSelectToUnit(unit)"
                                >
                                    {{ unit.unit }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
